// Domain · TransactionRoute
//
// Canonical "load → unload" route renderer. Replaces inline
// `${from} → ${to}` template strings and bespoke `<span>{a}{" → "}{b}</span>`
// snippets that were drifting per consumer (em-dash vs EmptyValue vs raw
// `"—"` fallback, different separator characters, divergent missing-side
// behavior).
//
// Behavior:
//   - Both sides absent  → single `<EmptyValue />`
//   - One side absent    → arrow with `<EmptyValue />` on the missing side
//   - Both sides present → `<from><separator><to>`
//
// Each side accepts a `{ id, short_name }` company ref — the component
// renders `<EntityLink type="company" />` per side so the operator gets
// the hover summary card and routing for free. Pass `null` when the
// side is unknown / absent.
//
// All visual values come from `@ds/tokens/palette`.

import { EmptyValue } from "@ds/display/EmptyValue";
import { useEntityLinkRenderer, type EntityLinkRenderer } from "@ds/display/entityLinkRendererContext";

// ─── Inputs (data props — the model) ─────────────────────────────────────

/** Minimal company ref accepted by TransactionRoute. Wider company
 *  shapes (`full_name`, `nip`, etc) get truncated to the (id, short_name)
 *  pair the EntityLink needs — the type's structural compatibility lets
 *  callers pass `row.load_place_company` directly without re-shaping. */
export interface RouteCompanyRef {
  id: number;
  short_name?: string | null;
}

export interface TransactionRouteInput {
  /** Load-side company (the "from" of the route). */
  from: RouteCompanyRef | null | undefined;
  /** Unload-side company (the "to" of the route). */
  to:   RouteCompanyRef | null | undefined;
}

// ─── Actions (configurable affordances) ──────────────────────────────────

export interface TransactionRouteActions {
  /** Override the inter-endpoint separator. Default: a single space.
   *  The visual "→" between the two sides comes from each EntityLink's
   *  own trailing-arrow affordance, so this primitive intentionally does
   *  NOT inject an arrow of its own (it'd render as a duplicate). */
  separator?: string;
  /** `data-testid` on the root span. Default: `"transaction-route"`. */
  testId?: string;
}

// ─── Component contract ──────────────────────────────────────────────────

export type TransactionRouteProps = TransactionRouteInput & TransactionRouteActions;

// ─── Implementation ──────────────────────────────────────────────────────

function renderEndpoint(c: RouteCompanyRef | null | undefined, renderLink: EntityLinkRenderer) {
  if (!c || c.id == null) return <EmptyValue />;
  return renderLink({
    type: "company",
    id: c.id,
    label: c.short_name ?? `#${c.id}`,
    variant: "inline",
  });
}

export function TransactionRoute({
  from,
  to,
  separator = " ",
  testId = "transaction-route",
}: TransactionRouteProps) {
  const renderLink = useEntityLinkRenderer();
  const hasFrom = !!from && from.id != null;
  const hasTo   = !!to   && to.id   != null;

  if (!hasFrom && !hasTo) {
    return <EmptyValue testId={testId} />;
  }

  return (
    <span data-testid={testId}>
      {renderEndpoint(from, renderLink)}
      {separator}
      {renderEndpoint(to, renderLink)}
    </span>
  );
}
